import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Grid, Typography, Divider, Badge } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faCreditCard, faMapPin, faPhone } from '@fortawesome/pro-light-svg-icons';
import { Button, Spinner } from 'components/UI';
import useStyles from './styles/PayByLinkStyles';
import AmountTable from './UI/AmountTable';
import CuddlesLogo from './UI/CuddlesLogo';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPayByLink, resetSaveTipError, saveTip } from 'actions/payByLink/payByLinkActions';
import { RootState } from 'store';
import { showError } from '@spike/notifications-action';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import InactivityModal from './UI/InactivityModal';
import PaymentSuccess from './UI/PaymentSuccess';
import { AlertBox } from 'components/UI/Alerts';
import TipSection from './UI/TipSection';
import OrderDetails from './UI/OrderDetails';
import NotFound from 'components/UI/NotFound';
import { round } from 'lodash';
import { formatPhoneNumber } from '@spike/phone-utils';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo';
import AlertMessage from 'components/UI/V3/AlertMessage';
import CreditCardPayment from 'components/Checkout2/CreditCardPayment2';
interface PayByLinkProps {
    uuid: string;
}
type TipType = string | 'none' | 'custom';

const base_url = `${process.env.REACT_APP_HOST_URL}`;

export const PayByLink: FunctionComponent<PayByLinkProps> = ({ uuid }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const {
        paymentLink,
        loading: loadingInvoice,
        fetchError,
        saveTipError
    } = useSelector((state: RootState) => state.payByLink);

    const [showInactivityModal, setShowInactivityModal] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);
    const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
    const [showCustomTipEdit, setShowCustomTipEdit] = useState<boolean>(true);
    const [selectedTip, setSelectedTip] = useState<TipType>('none');
    const [customTipType, setCustomTipType] = useState<'fixed' | 'percentage'>('fixed');
    const [customTipAmount, setCustomTipAmount] = useState<number>(0);
    const [surcharge, setSurcharge] = useState(0);

    const marketplace = paymentLink?.marketplace;
    const tips = marketplace?.tip_rates ?? [];

    const blockTimeoutInSeconds = Number(process.env.REACT_APP_BLOCK_TIMEOUT_SECONDS || 60);

    /*Dropin */
    const [showCreditCardPayment, setShowCreditCardPayment] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showErrorPayment, setShowErrorPayment] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [completing, setCompleting] = useState(false);
    const errorPaymentMessage = (
        <span>
            <strong>Payment cannot be processed.</strong> Please try a different card or contact{' '}
            {marketplace?.business_name}. If you have any questions, contact{' '}
            <strong>{marketplace?.business_name}</strong> at <strong>{marketplace?.phone}</strong>.
        </span>
    );

    const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

    const isAppointment = paymentLink?.invoice.appt_date && paymentLink.invoice.appt_date !== '';

    const isPartialPayment = useMemo(() => {
        if (!paymentLink?.invoice || !paymentLink?.amount) return false;
        return Number(paymentLink.amount) < Number(paymentLink.invoice.balance);
    }, [paymentLink?.amount, paymentLink?.invoice?.balance]);

    useEffect(() => {
        dispatch(fetchPayByLink(uuid));
    }, [uuid]);

    useEffect(() => {
        if (paymentLink) {
            setLoadingPage(false);
            if (paymentLink.invoice && !paymentLink.invoice.appt_date) {
                setOrderDetailsOpen(true);
            }
        }
    }, [paymentLink]);

    useEffect(() => {
        const events = ['keydown'];

        events.forEach(event => {
            window.addEventListener(event, resetInactivityTimer);
        });

        resetInactivityTimer(); // Initialize the timer when the component mounts

        return () => {
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
            events.forEach(event => {
                window.removeEventListener(event, resetInactivityTimer);
            });
        };
    }, [paymentLink]);

    useEffect(() => {
        if (fetchError || saveTipError) {
            setLoadingPage(false);
            if (saveTipError) {
                dispatch(showError(saveTipError));
                dispatch(resetSaveTipError());
            }
        }
    }, [fetchError, saveTipError]);

    const resetInactivityTimer = () => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }
        inactivityTimerRef.current = setTimeout(() => {
            setShowInactivityModal(true);
            setShowCreditCardPayment(false);
            setShowErrorPayment(false);
            setShowSuccess(false);
        }, blockTimeoutInSeconds * 1500);
    };

    const completeHandler = () => {
        setShowCreditCardPayment(true);
        resetInactivityTimer();
        setShowErrorPayment(false);
    };

    const paymentCompleteHandler = (paidAmount: number) => {
        setSuccessMessage(paidAmount.toFixed(2));
        setShowSuccess(true);
        setShowCreditCardPayment(false);
    };

    const paymentBackHandler = () => {
        resetInactivityTimer();
        setShowCreditCardPayment(false);
    };

    const saveTipAmount = async (
        amount: number | null,
        percentage: string | null,
        tipsToDestroy: Array<{ id: number; staff_id: number }> = []
    ) => {
        if (paymentLink?.invoice.id && marketplace && paymentLink.marketplace.id) {
            await dispatch(
                saveTip(
                    paymentLink.invoice.id,
                    paymentLink.marketplace.id,
                    uuid,
                    amount !== null ? amount.toString() : null,
                    percentage,
                    paymentLink.token,
                    tipsToDestroy
                )
            );
        }
    };

    const handleTipSelect = (tip: TipType) => {
        if (showCreditCardPayment) return;
        setSelectedTip(tip);
        if (tip === 'none') {
            setCustomTipAmount(0);
            setShowCustomTipEdit(false);
            // Clearing the tip must also destroy the groomer's preset per-staff tip
            // records; otherwise the backend recalculates the tip from those stale
            // rows and reverts the amount back to the preset (SU-2289).
            const tipsToDestroy = (paymentLink?.invoice.tips ?? []).map(persistedTip => ({
                id: persistedTip.id,
                staff_id: persistedTip.staff_id
            }));
            saveTipAmount(0, null, tipsToDestroy);
        } else if (tip === 'custom') {
            setShowCustomTipEdit(true);
        } else {
            setCustomTipType('fixed');
            setShowCustomTipEdit(false);
            const selectedTipAmount = calculateTipAmount(
                Number(tip.replace('%', '')),
                Number(paymentLink?.invoice.subtotal)
            );
            saveTipAmount(null, tip.replace('%', ''));
        }
    };

    const handleCustomTipConfirm = () => {
        if (customTipAmount > 0) {
            if (customTipType === 'percentage') {
                saveTipAmount(null, customTipAmount.toString());
            } else {
                saveTipAmount(customTipAmount, null);
            }
            setShowCustomTipEdit(false);
        } else {
            handleTipSelect('none');
        }
    };

    const handleCustomTipTypeSelect = (type: 'fixed' | 'percentage') => {
        setCustomTipType(type);
    };

    const handleCustomTipAmountChange = (value: number) => {
        setCustomTipAmount(value);
    };

    const handleCustomTipCancel = () => {
        if (customTipAmount > 0) {
            setShowCustomTipEdit(false);
        } else {
            handleTipSelect('none');
        }
    };

    const calculateTipAmount = (percentage: number, subtotal: number): string => {
        return round((Number(subtotal) * percentage) / 100, 2)
            .toFixed(2)
            .toString();
    };

    const handleEditCustomTip = () => {
        setShowCustomTipEdit(true);
    };

    const toggleOrderDetails = () => {
        setOrderDetailsOpen(!orderDetailsOpen);
    };

    const changeSurchargeHandler = (surcharge: number) => {
        setSurcharge(surcharge);
    };

    useEffect(() => {
        if (paymentLink) {
            const invoiceTipAmount = Number(paymentLink.invoice.tip_amount);
            const invoiceTipPercentage = paymentLink.invoice.tip_percentage;
            let initialTip: TipType = 'none';

            // Check if the invoice tip amount matches any of the default tips
            tips.forEach(tip => {
                const calculatedTipAmount = calculateTipAmount(Number(tip.value), Number(paymentLink.invoice.subtotal));
                if (Number(calculatedTipAmount) === invoiceTipAmount) {
                    initialTip = `${tip.value}%`;
                }
            });

            if (initialTip === 'none') {
                if (invoiceTipPercentage !== null) {
                    initialTip = 'custom';
                    setCustomTipAmount(Number(invoiceTipPercentage));
                    setCustomTipType('percentage');
                    setShowCustomTipEdit(false);
                } else if (invoiceTipAmount > 0) {
                    initialTip = 'custom';
                    setCustomTipAmount(invoiceTipAmount);
                    setShowCustomTipEdit(false); // Show the amount container instead of the edit container
                }
            }

            setSelectedTip(initialTip);
        }
    }, [paymentLink, tips]);

    if (loadingPage) {
        return <Spinner className={classes.spinnerStyle} />;
    }

    let content: JSX.Element | null = null;

    if (paymentLink) {
        const totalAmountRows = [
            {
                label: 'Subtotal',
                value: `$${Number(paymentLink.invoice.subtotal).toFixed(2)}`
            },
            ...(Number(paymentLink.invoice.configured_discount_amount || 0) > 0
                ? [
                      {
                          label: 'Automatic Discounts',
                          value: `-$${Number(paymentLink.invoice.configured_discount_amount).toFixed(2)}`
                      }
                  ]
                : []),
            ...(Number(paymentLink.invoice.surcharge_amount || 0) > 0
                ? [
                      {
                          label: 'Surcharge',
                          value: `$${Number(paymentLink.invoice.surcharge_amount).toFixed(2)}`
                      }
                  ]
                : []),
            // Item in-line discounts
            ...(Number(paymentLink.invoice.item_level_discount_amount || 0) > 0
                ? [
                      {
                          label: 'Item in-line discounts',
                          value: `-$${Number(paymentLink.invoice.item_level_discount_amount).toFixed(2)}`
                      }
                  ]
                : []),
            // Promo Code
            ...(Number(paymentLink.invoice.promocode_discount_amount || 0) > 0
                ? [
                      {
                          label: `Promo Code ${paymentLink.invoice.promocodes
                              .map(pc => pc.name.toUpperCase())
                              .join(', ')}`,
                          value: `-$${Number(paymentLink.invoice.promocode_discount_amount).toFixed(2)}`
                      }
                  ]
                : []),
            {
                label: 'Sale Tax',
                value: `$${Number(paymentLink.invoice.tax_amount || 0).toFixed(2)}`
            }
        ];

        const orderDetailsRows = paymentLink.invoice.invoice_lines
            ? paymentLink.invoice.invoice_lines
                  .filter(line => line.description) // Filter out lines with no description
                  .map(line => ({
                      label: line.staff_name
                          ? `${line.description} by <strong>${line.staff_name}</strong>`
                          : line.description!,
                      quantity: line.quantity,
                      discountedValue: `$${line.amount}`,
                      value: `$${line.total}`
                  }))
            : [];

        const businessName = paymentLink.invoice.business_name || '';
        const businessLogoUrl = paymentLink.invoice.logo_url || '';

        content = (
            <Box className={classes.body}>
                <Box className={classes.storeInfo}>
                    <Box className={classes.logo}>
                        {businessLogoUrl ? (
                            <img
                                src={businessLogoUrl}
                                alt="Store Logo"
                            />
                        ) : (
                            <Typography className={classes.storeName}>{businessName}</Typography>
                        )}
                    </Box>
                    <Box className={classes.storeDetails}>
                        <Typography className={classes.storeName}>{businessName}</Typography>
                        <Typography className={classes.storeContact}>
                            <FontAwesomeIcon
                                className={classes.storeInfoIcon}
                                icon={faPhone}
                                style={{ marginRight: 8 }}
                            />
                            {marketplace?.phone
                                ? formatPhoneNumber(marketplace.phone, marketplace.addresses?.[0]?.country ?? 'US')
                                : 'N/A'}
                        </Typography>
                        <Typography className={classes.storeAddress}>
                            <FontAwesomeIcon
                                className={classes.storeInfoIcon}
                                icon={faMapPin}
                                style={{ marginRight: 8 }}
                            />
                            {paymentLink.invoice.address || 'No Address'}
                        </Typography>
                    </Box>
                </Box>
                {isAppointment && (
                    <Box className={classes.boxDefault}>
                        <Box className={classes.bookingDetails}>
                            <Badge
                                classes={{ badge: classes.customBadge }}
                                badgeContent={<FontAwesomeIcon icon={faCalendarAlt} />}
                            />
                            <Box className={classes.bookingDetailsInfo}>
                                <Typography
                                    variant="h2"
                                    className={classes.bookingTitle}
                                >
                                    Booking Details
                                </Typography>
                                <Typography variant="body2">
                                    {moment(paymentLink.invoice.appt_date).format('dddd, DD MMM YYYY - hh:mmA')}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {!showCreditCardPayment && isAppointment && !isPartialPayment && (
                    <TipSection
                        tips={tips}
                        selectedTip={selectedTip}
                        handleTipSelect={handleTipSelect}
                        showCustomTipEdit={showCustomTipEdit}
                        customTipType={customTipType}
                        handleCustomTipTypeSelect={handleCustomTipTypeSelect}
                        customTipAmount={customTipAmount}
                        handleCustomTipAmountChange={handleCustomTipAmountChange}
                        handleCustomTipConfirm={handleCustomTipConfirm}
                        handleCustomTipCancel={handleCustomTipCancel}
                        calculateTipAmount={calculateTipAmount}
                        subtotal={Number(paymentLink.invoice.subtotal)}
                        classes={classes}
                        handleEditCustomTip={handleEditCustomTip}
                    />
                )}

                <Box className={classes.invoiceContent}>
                    {loadingInvoice ? (
                        <Box className={classes.spinnerContent}>
                            <Spinner />
                        </Box>
                    ) : (
                        <>
                            <AmountTable
                                title="Total Amount"
                                rows={totalAmountRows}
                                trailingRows={[
                                    {
                                        label: 'Tip',
                                        value: `$${Number(paymentLink.invoice.tip_amount).toFixed(2)}` || '$0.00'
                                    }
                                ]}
                                total={{
                                    label: 'Total',
                                    value: `$${Number(paymentLink.invoice.grand_total).toFixed(2)}`
                                }}
                                dueTotal={{
                                    label: 'Due Total',
                                    value: `$${Number(paymentLink.invoice.balance).toFixed(2)}`
                                }}
                                deposit={
                                    Number(paymentLink.invoice.deposits_amount) > 0
                                        ? {
                                              label: 'Deposit',
                                              value: `$${Number(paymentLink.invoice.deposits_amount).toFixed(2)}`
                                          }
                                        : undefined
                                }
                                payments={
                                    Number(paymentLink.invoice.payments_total) -
                                        Number(paymentLink.invoice.deposits_amount) >
                                    0
                                        ? {
                                              label: 'Payments',
                                              value: `$${(
                                                  Number(paymentLink.invoice.payments_total) -
                                                  Number(paymentLink.invoice.deposits_amount)
                                              ).toFixed(2)}`
                                          }
                                        : undefined
                                }
                                refunds={
                                    Number(paymentLink.invoice.refunds_total) > 0
                                        ? {
                                              label: 'Refunds',
                                              value: `$${paymentLink.invoice.refunds_total}`
                                          }
                                        : undefined
                                }
                                partialPayment={
                                    isPartialPayment
                                        ? {
                                              label: 'Amount to pay',
                                              value: `$${(Number(paymentLink.amount) + surcharge).toFixed(2)}`
                                          }
                                        : undefined
                                }
                                surcharge={
                                    surcharge === 0
                                        ? undefined
                                        : {
                                              label: 'Surcharge',
                                              value: `$${surcharge.toFixed(2)}`
                                          }
                                }
                            />

                            <Grid
                                item
                                xs={12}
                                className={classes.divider}
                            >
                                <Divider />
                            </Grid>

                            {!showCreditCardPayment && (
                                <OrderDetails
                                    orderDetailsRows={orderDetailsRows}
                                    orderDetailsOpen={orderDetailsOpen}
                                    toggleOrderDetails={toggleOrderDetails}
                                    classes={classes}
                                />
                            )}

                            {(showCreditCardPayment || showErrorPayment) && (
                                <Box className={classes.paymentBlock}>
                                    <Typography
                                        variant="h2"
                                        className={classes.bookingTitle}
                                    >
                                        Payment
                                    </Typography>
                                    <Typography
                                        className={classes.subtitle}
                                        variant="body2"
                                    >
                                        Safe and encrypted payments
                                    </Typography>
                                </Box>
                            )}

                            {showErrorPayment && (
                                <AlertBox
                                    type="error"
                                    className={classes.alertBox}
                                    text={errorPaymentMessage}
                                    actionable={false}
                                />
                            )}

                            {!showCreditCardPayment && (
                                <Box className={classes.payButtonBlock}>
                                    <Button
                                        color="green"
                                        size="large"
                                        label={'Pay'}
                                        id="pay_by_link_confirm_pay_button"
                                        className={classes.button}
                                        onClick={completeHandler}
                                        loading={completing}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box className={classes.root}>
            {!fetchError && (
                <Box className={classes.titleContainer}>
                    {(showCreditCardPayment || showErrorPayment) && (
                        <Box
                            className={classes.backButton}
                            onClick={() => {
                                setShowCreditCardPayment(false);
                                setShowErrorPayment(false);
                            }}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                            <Typography className={classes.backButtonText}>Back</Typography>
                        </Box>
                    )}
                    <Typography className={classes.title}>Pay By Link</Typography>
                </Box>
            )}
            <Box className={classes.wrapper}>
                {fetchError ? (
                    <NotFound
                        customContainerClass={classes.notFoundContainer}
                        title="Oops! Payment Link Not Found"
                        subtitle="Oops! We couldn't find the payment link you're looking for."
                    />
                ) : showSuccess && successMessage ? (
                    <PaymentSuccess
                        paidAmount={successMessage}
                        paymentDate={moment().format('dddd, DD MMM YYYY')}
                    />
                ) : showInactivityModal ? (
                    <InactivityModal />
                ) : (
                    content
                )}
            </Box>
            {showCreditCardPayment && paymentLink && uuid && isPartialPayment && (
                <Box className={classes.wrapper}>
                    <Box className={classes.body}>
                        <AlertMessage
                            icon={faCircleInfo}
                            bdColor="#BCB8AE"
                            bgColor="#F8F5F1"
                            iconColor="#BAA997"
                        >
                            This Pay by Link will process a partial payment of the total amount.
                        </AlertMessage>
                    </Box>
                </Box>
            )}
            {showCreditCardPayment && paymentLink && uuid && paymentLink.invoice.id != null && (
                <Box className={classes.wrapper}>
                    <Box className={classes.body}>
                        <CreditCardPayment
                            invoiceUuid={paymentLink?.invoice.uuid}
                            invoiceId={paymentLink.invoice.id}
                            clientId={paymentLink?.invoice.customer_id}
                            amount={Number(paymentLink?.amount ?? '0')}
                            paymentLink={paymentLink}
                            className={classes.creditCardPaymentContainer}
                            onSuccess={paymentCompleteHandler}
                            onBack={paymentBackHandler}
                            onChangeSurcharge={changeSurchargeHandler}
                        />
                    </Box>
                </Box>
            )}
            {!fetchError && (
                <Box className={classes.footer}>
                    <Typography className={classes.footerText}>Powered by</Typography>
                    <CuddlesLogo className={classes.cuddlesLogo} />
                </Box>
            )}
        </Box>
    );
};

export default PayByLink;
